// Hooks
import { useEffect, useRef, useCallback } from 'react'
import useKanban from '@/hooks/useKanban'

// Components
import TableFilters from '../table/TableFilters'
import TableColumns from '../table/TableColumns'

// Mantine
import { Button, Tooltip, Badge, Loader } from '@mantine/core'

// Icons
import { IconRefresh } from '@tabler/icons-react'
import classNames from 'classnames'

// UI
import LocationSelect from '../LocationSelect'

function KanbanColumnBody({ column, ItemComponent, fetchMore, isFetchingMore, refetchData, onItemOpen }) {
    const sentinelRef = useRef(null)
    const hasMore = column.items.length < column.total

    useEffect(() => {
        if (!hasMore || !sentinelRef.current) return

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    fetchMore(column.slug)
                }
            },
            { threshold: 0 }
        )

        observer.observe(sentinelRef.current)
        return () => observer.disconnect()
    }, [hasMore, column.slug, column.items.length])

    return (
        <div className="flex-1 overflow-y-scroll overflow-x-hidden overscroll-y-prevent p-5 flex flex-col gap-5">
            {column.items.length === 0 && (
                <div className="flex-1 flex items-center justify-center">
                    <span className="text-black opacity-30">
                        Aucun élément
                    </span>
                </div>
            )}

            {column.items.map((item) => (
                <ItemComponent
                    key={item.id}
                    item={item}
                    onMutate={refetchData}
                    onOpen={onItemOpen}
                />
            ))}

            {hasMore && (
                <div ref={sentinelRef} className="flex justify-center py-2">
                    {isFetchingMore && <Loader size="sm" />}
                </div>
            )}
        </div>
    )
}

export default function Kanban({
    availableColumns = [],
    availableFilters,
    engineApiEndpoint,
    getColumnProps,
    ItemComponent,
    withLocation = false,
    withLocationAll = false,
    searchable = false,
    onItemOpen,
    refetchRef,
}) {
    const {
        kanbanState,
        kanbanData,
        isFetching,
        refetchData,
        fetchMore,
        fetchingMoreColumns,
        changeFilters,
        columns,
        query,
        setQuery,
        resetFilters,
        changeColumns,
        selectView,
        resetView,
        changeLocationId,
    } = useKanban({
        availableColumns,
        availableFilters,
        engineApiEndpoint,
    })

    if (refetchRef) refetchRef.current = refetchData

    return (
        <div className="overscroll-contain">
            <div className="h-[60px] px-5 py-3 border-b flex items-center justify-between">
                <TableFilters
                    availableFilters={availableFilters}
                    tableState={kanbanState}
                    changeFilters={changeFilters}
                    resetFilters={resetFilters}
                />

                <div className="flex items-center gap-2">
                    {withLocation && (
                        <LocationSelect
                            value={kanbanState.location_id ?? 'all'}
                            onChange={changeLocationId}
                            withAll={withLocationAll}
                        />
                    )}

                    <TableColumns reorderable={false}
                        availableColumns={availableColumns}
                        tableState={kanbanState}
                        changeColumns={changeColumns}
                    />

                    <Tooltip label="Rafraichir les données">
                        <Button
                            variant="default"
                            onClick={refetchData}
                            loading={isFetching}
                        >
                            <IconRefresh size={16} />
                        </Button>
                    </Tooltip>
                </div>
            </div>

            <div className="h-[calc(100dvh-158px)] xl:h-[calc(100dvh-106px)] overflow-x-scroll overflow-y-hidden overscroll-contain flex gap-5 p-5">
                {kanbanData &&
                    kanbanData.map((column) => {
                        const columnProps = getColumnProps(column.slug) || {}
                        const subtitle = columnProps.subtitle

                        const headerClasses = classNames(
                            'flex justify-between items-center p-5 border-b',
                            columnProps?.bgColor,
                            columnProps?.textColor
                        )

                        return (
                            <div
                                key={column.slug}
                                className="w-[400px] border rounded-xl flex flex-col shrink-0 overflow-hidden bg-white"
                            >
                                <div className={headerClasses}>
                                    <div className="flex flex-col">
                                        <span className="font-medium">
                                            {columnProps.name ?? column.slug}
                                        </span>

                                        <span className="text-sm opacity-80">
                                            {subtitle}
                                        </span>
                                    </div>

                                    <Badge
                                        color="gray"
                                        size="lg"
                                        variant="light"
                                    >
                                        {column.total}
                                    </Badge>
                                </div>

                                <KanbanColumnBody
                                    column={column}
                                    ItemComponent={ItemComponent}
                                    fetchMore={fetchMore}
                                    isFetchingMore={fetchingMoreColumns[column.slug]}
                                    refetchData={refetchData}
                                    onItemOpen={onItemOpen}
                                />
                            </div>
                        )
                    })}
            </div>
        </div>
    )
}
